import type { components } from '@/api/client';
import type { DeliveryRecordDto } from '@/features/patient-record/api/questionnaires';
import type {
  DeliveryRecordViewModel,
  DeliveryStatus,
  QuestionnaireDelivery,
  QuestionnaireStatus,
  QuestionnaireViewModel,
} from '@/features/patient-record/types';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type QuestionnaireDto = components['schemas']['Questionnaire'];

const KNOWN_STATUS: ReadonlySet<QuestionnaireStatus> = new Set<QuestionnaireStatus>([
  'NOT_SENT',
  'SENT',
  'OPENED',
  'COMPLETED',
  'EXPIRED',
  'REVOKED',
]);

const KNOWN_DELIVERY: ReadonlySet<QuestionnaireDelivery> = new Set<QuestionnaireDelivery>([
  'EMAIL',
  'SMS',
  'URL',
]);

export function mapQuestionnaire(dto: QuestionnaireDto): QuestionnaireViewModel {
  return {
    id: dto.id,
    templateId: dto.template_id,
    templateName: dto.template_name,
    status: narrowEnum<QuestionnaireStatus>(dto.status, KNOWN_STATUS, 'NOT_SENT'),
    sentAt: nullableString(dto.sent_at),
    expiresAt: nullableString(dto.expires_at),
    completedAt: nullableString(dto.completed_at),
    deliveryMethod: narrowEnum<QuestionnaireDelivery>(dto.delivery_method, KNOWN_DELIVERY, null),
    // `job_execution_id` is newer than the generated OpenAPI schema; read it defensively until the
    // contract is regenerated.
    jobExecutionId: (dto as { job_execution_id?: number | null }).job_execution_id ?? null,
  };
}

export function mapQuestionnaires(dtos: readonly QuestionnaireDto[]): QuestionnaireViewModel[] {
  return dtos.map(mapQuestionnaire);
}

const KNOWN_DELIVERY_STATUS: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>([
  'QUEUED',
  'SENT',
  'DELIVERED',
  'BOUNCED',
  'COMPLAINED',
  'FAILED',
  'SUPPRESSED',
]);

export function mapDelivery(dto: DeliveryRecordDto): DeliveryRecordViewModel {
  return {
    id: dto.id ?? '',
    channel: nullableString(dto.channel),
    recipient: nullableString(dto.recipient),
    status: narrowEnum<DeliveryStatus>(dto.status, KNOWN_DELIVERY_STATUS, 'QUEUED'),
    errorCode: nullableString(dto.error_code),
    errorMessage: nullableString(dto.error_message),
    attemptNo: dto.attempt_no ?? null,
    requestedAt: nullableString(dto.requested_at),
    sentAt: nullableString(dto.sent_at),
    statusUpdatedAt: nullableString(dto.status_updated_at),
  };
}

/** Backend returns attempts newest-first (requested_at desc); preserved here. */
export function mapDeliveries(dtos: readonly DeliveryRecordDto[]): DeliveryRecordViewModel[] {
  return dtos.map(mapDelivery);
}
